import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AddToLibraryDialog from "@/components/AddToLibraryDialog";
import { ScopeBadge } from "@/components/ScopeBadge";
import { richTextToPlain } from "@/components/RichTextEditor";
import { Plus, Search, Workflow, Trash2, Pencil, BookmarkPlus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

export default function Sequences() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(ALL);
  const [deleteId, setDeleteId] = useState(null);
  const [addItem, setAddItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (scope !== ALL) params.scope = scope;
      const { data } = await api.get("/sequences", { params });
      setItems(data);
    }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [q, scope]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const isPA = user?.role === "platform_admin";
  const canEdit = (s) => s.scope === "platform" ? isPA
    : isPA ? false
    : s.scope === "personal" ? s.owner_id === user?.user_id
    : (user?.role === "admin" || user?.role === "coach") && s.gym_id === user?.gym_id;

  const doDelete = async () => {
    try { await api.delete(`/sequences/${deleteId}`); toast.success("Deleted"); setDeleteId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 pb-24 md:pb-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-1">Flowcharts</p>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Sequences</h1>
        </div>
        <Button data-testid="new-sequence-btn" onClick={() => navigate("/sequences/new")} className="bg-primary hover:bg-primary/90 text-white rounded">
          <Plus className="h-4 w-4 mr-1.5" /> New sequence
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="sequence-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sequences…" className="pl-9 bg-card border-border" />
        </div>
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="sequence-filter-scope" className="w-[150px] bg-card border-border"><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All owners</SelectItem>
            <SelectItem value="gym">🏫 Gym</SelectItem>
            <SelectItem value="personal">👤 Personal</SelectItem>
            <SelectItem value="platform">🌐 Platform</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-40 rounded-xl bg-card border border-border animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <Workflow className="h-10 w-10 text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl font-semibold">No sequences yet</h3>
          <p className="text-muted-foreground mt-1 mb-5 text-sm">Map out your game — build a flowchart of techniques.</p>
          <Button data-testid="empty-new-sequence-btn" onClick={() => navigate("/sequences/new")} className="bg-primary text-white"><Plus className="h-4 w-4 mr-1.5" /> New sequence</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} data-testid={`sequence-card-${s.id}`}
              className="group rounded-xl bg-card border border-border hover:border-primary/60 transition-colors duration-300 p-5 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <ScopeBadge scope={s.scope} className="border border-border text-muted-foreground" />
                <span className="text-xs text-muted-foreground flex items-center gap-1.5"><Workflow className="h-3.5 w-3.5" /> {(s.nodes || []).length} moves</span>
              </div>
              <h3 className="font-heading font-semibold text-lg cursor-pointer hover:text-primary leading-tight" onClick={() => navigate(`/sequences/${s.id}`)}>{s.name}</h3>
              {s.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{richTextToPlain(s.description)}</p>}
              <div className="flex items-center gap-1 mt-4 pt-3 border-t border-border">
                <Button data-testid={`open-sequence-${s.id}`} variant="ghost" size="sm" onClick={() => navigate(`/sequences/${s.id}`)} className="text-primary hover:text-primary hover:bg-primary/10 h-10 sm:h-8">Open <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
                <div className="flex-1" />
                {s.scope === "platform" && !isPA ? (
                  <Button data-testid={`add-to-library-seq-${s.id}`} variant="ghost" size="sm" onClick={() => setAddItem(s)}
                    className="text-primary hover:text-primary hover:bg-primary/10 h-10 sm:h-8 px-2">
                    <BookmarkPlus className="h-3.5 w-3.5 mr-1" /> Add to Library
                  </Button>
                ) : canEdit(s) && <>
                  <Button data-testid={`edit-sequence-${s.id}`} variant="ghost" size="sm" onClick={() => navigate(`/sequences/${s.id}/edit`)} className="text-muted-foreground hover:text-white h-10 sm:h-8 px-2"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`delete-sequence-${s.id}`} variant="ghost" size="sm" onClick={() => setDeleteId(s.id)} className="text-muted-foreground hover:text-destructive h-10 sm:h-8 px-2"><Trash2 className="h-3.5 w-3.5" /></Button>
                </>}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddToLibraryDialog open={!!addItem} onOpenChange={(o) => !o && setAddItem(null)} item={addItem} kind="sequence" onDone={load} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete sequence?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-sequence" onClick={doDelete} className="bg-destructive hover:bg-destructive/90 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
